import { FC, useState, useEffect } from "react";
import {
  Card,
  Table,
  Tag,
  Button,
  Input,
  Space,
  Tabs,
  DatePicker,
  Spin,
  Alert,
  Modal,
  Descriptions,
  message,
  Popconfirm,
  Checkbox,
  Segmented,
  Dropdown,
  InputNumber,
  Tooltip,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  DeleteOutlined,
  FileTextOutlined,
  UserOutlined,
  CalendarOutlined,
  DollarOutlined,
  FilePdfOutlined,
  CheckCircleOutlined,
  LinkOutlined,
  DownloadOutlined,
  SendOutlined,
  CopyOutlined,
  DatabaseOutlined,
  MoreOutlined,
  EyeOutlined,
  MailOutlined,
  StopOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import PageMeta from "~/components/common/meta";
import { BillingSubNav } from "~/components/admin/billing";
import {
  useInvoices,
  useDeleteInvoice,
  useBulkSendInvoices,
  getInvoicesForExport,
} from "~/services/billing/billing.queries";
import { Invoice } from "~/components/admin/billing/types";
import { getInvoicePdf, createPaymentLink, getPaymentLink } from "~/services/billing/billing.api";
import { getInvoice } from "~/services/billing/billing.api";
import { useAuth } from "~/store/auth.store";
import {
  useStorageInvoices,
  useDeleteStorageInvoice,
  useMarkStorageInvoiceAsSent,
  useMarkStorageInvoiceAsPaid,
  useUpdateStorageInvoice,
} from "~/services/storage/storage.queries";
import {
  StorageInvoice,
  StorageInvoiceStatus,
  getStorageInvoices,
} from "~/services/storage/storage.api";
import { getClientName } from "~/utils";

const { RangePicker } = DatePicker;

export const ScreenInvoicing: FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invoiceType, setInvoiceType] = useState<"service" | "storage">("service");
  const [activeTab, setActiveTab] = useState<string>("all");
  const [searchText, setSearchText] = useState<string>("");
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [deletingInvoiceId, setDeletingInvoiceId] = useState<string | null>(
    null
  );
  const [isPdfLoading, setIsPdfLoading] = useState(false);

  // CSV Export state
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  // Send Invoice Modal state
  const [sendModalVisible, setSendModalVisible] = useState(false);
  const [sendingInvoices, setSendingInvoices] = useState(false);
  const [invoiceToPreview, setInvoiceToPreview] = useState<any>(null);
  const [previewPaymentLink, setPreviewPaymentLink] = useState<string | null>(null);
  const [generatingLink, setGeneratingLink] = useState(false);

  // Storage invoice state
  const [storageInvoicePage, setStorageInvoicePage] = useState(1);
  const [storageInvoicePageSize, setStorageInvoicePageSize] = useState(50);
  const [selectedStorageInvoiceIds, setSelectedStorageInvoiceIds] = useState<string[]>([]);
  const [isExportingStorage, setIsExportingStorage] = useState(false);
  const [storagePaidModalVisible, setStoragePaidModalVisible] = useState(false);
  const [selectedStorageInvoiceForPaid, setSelectedStorageInvoiceForPaid] = useState<StorageInvoice | null>(null);
  const [storagePaidAmount, setStoragePaidAmount] = useState<number>(0);
  const [storagePaidDate, setStoragePaidDate] = useState<dayjs.Dayjs>(dayjs());


  // Handle QuickBooks connection

  // Map frontend tabs to API status values
  const getApiStatus = (tab: string) => {
    switch (tab) {
      case "pending":
        return "draft";
      case "invoiced":
        return "sent";
      case "paid":
        return "paid";
      case "overdue":
        return "overdue";
      default:
        return undefined;
    }
  };

  // Map frontend tabs to storage invoice status
  const getStorageApiStatus = (tab: string): StorageInvoiceStatus | undefined => {
    switch (tab) {
      case "pending":
        return StorageInvoiceStatus.DRAFT;
      case "invoiced":
        return StorageInvoiceStatus.SENT;
      case "paid":
        return StorageInvoiceStatus.PAID;
      default:
        return undefined;
    }
  };

  // Use real API call with correct status mapping
  const {
    data: invoicesData,
    isLoading,
    error,
    refetch,
  } = useInvoices({
    status: getApiStatus(activeTab),
    page: 1,
    limit: 50,
  });

  // Storage invoices query
  const {
    data: storageInvoicesData,
    isLoading: isLoadingStorage,
    refetch: refetchStorage,
  } = useStorageInvoices({
    status: getStorageApiStatus(activeTab),
    page: storageInvoicePage,
    limit: storageInvoicePageSize,
  });

  // Storage invoice mutations
  const deleteStorageInvoiceMutation = useDeleteStorageInvoice();
  const markStorageAsSent = useMarkStorageInvoiceAsSent();
  const markStorageAsPaid = useMarkStorageInvoiceAsPaid();
  const updateStorageInvoiceMutation = useUpdateStorageInvoice();

  // Handle refresh from create invoice page using sessionStorage
  useEffect(() => {
    // Check if we're coming from create invoice page
    const isFromCreateInvoice = sessionStorage.getItem("fromCreateInvoice");
    if (isFromCreateInvoice === "true") {
      refetch();
      message.success({
        content: "Invoice list refreshed! 📋",
        duration: 3,
      });
      sessionStorage.removeItem("fromCreateInvoice");
    }
  }, [refetch]);

  const deleteInvoiceMutation = useDeleteInvoice();
  const bulkSendMutation = useBulkSendInvoices();

  // Clear selection when changing tabs or invoice type
  useEffect(() => {
    setSelectedInvoiceIds([]);
    setSelectedStorageInvoiceIds([]);
  }, [activeTab, invoiceType]);

  // Generate CSV export filename
  const generateExportFilename = () => {
    const now = new Date();
    const dateStr = now.toISOString().split("T")[0]; // YYYY-MM-DD
    const timeStr = now.toTimeString().split(" ")[0].replace(/:/g, "-"); // HH-MM-SS
    const userName = user?.firstName
      ? `${user.firstName}${user.lastName ? "_" + user.lastName : ""}`
      : "User";
    return `QB_Invoice_Export_${dateStr}_${timeStr}_${userName}.csv`;
  };

  // Helper to format service type for display
  const formatServiceType = (serviceType: string) => {
    const typeMap: Record<string, string> = {
      COLLECTION: "Collection",
      DELIVERY: "Delivery",
      ON_SITE_SERVICE: "On-Site Service",
      WAREHOUSE_SERVICE: "Warehouse Service",
      FABRICATION_SERVICE: "Fabrication",
    };
    return typeMap[serviceType] || serviceType || "Services";
  };

  // QB memo character limit (conservative)
  const QB_MEMO_LIMIT = 1000;

  // Generate abridged memo from service tickets
  const generateMemo = (invoice: any, serviceTickets: any[]) => {
    if (!serviceTickets || serviceTickets.length === 0) {
      return invoice.notes || "";
    }

    const jobTicket = serviceTickets[0]?.jobTicket;
    const refNumber = jobTicket?.referenceNumber || "";
    const sidemark = jobTicket?.sidemark || "";
    const projectName = jobTicket?.project?.name || "";
    const lines: string[] = [];

    // Add sidemark and project at the top (if applicable)
    const headerParts = [];
    if (sidemark) headerParts.push(`Sidemark: ${sidemark}`);
    if (projectName) headerParts.push(`Project: ${projectName}`);
    if (headerParts.length > 0) {
      lines.push(headerParts.join(" | "));
    }

    // Add reference
    if (refNumber) {
      lines.push(`Ref #${refNumber}`);
    }

    // Add each service as a line
    serviceTickets.forEach((ticket: any, index: number) => {
      const serviceType = formatServiceType(ticket.serviceType);
      const locationName =
        ticket.location?.addressName ||
        ticket.location?.name ||
        ticket.tempLocationData?.name ||
        ticket.tempLocationData?.city ||
        "";
      const scope = ticket.scope || "";
      const suffix = serviceTickets.length > 1 ? `-${String(index + 1).padStart(2, "0")}` : "";

      // Format: "1) Collection @ Gallery XYZ - Pack artwork"
      const parts = [`${index + 1}) ${serviceType}`];
      if (locationName) parts.push(`@ ${locationName}`);
      if (scope) parts.push(`- ${scope}`);
      if (suffix) parts.push(suffix);

      lines.push(parts.join(" "));
    });

    // Join with semicolons for compact format
    let memo = lines.join("; ");

    // If memo is too long, truncate and add ellipsis
    if (memo.length > QB_MEMO_LIMIT) {
      memo = memo.substring(0, QB_MEMO_LIMIT - 3) + "...";
    }

    return memo;
  };

  // Helper to extract invoice number without INV prefix
  const getInvoiceNumberDigits = (invoiceNumber: string) => {
    if (!invoiceNumber) return "";
    // Remove "INV" prefix and any leading zeros, keep the rest (including suffixes like -01)
    return invoiceNumber.replace(/^INV/i, "");
  };

  // Generate CSV content for QuickBooks import
  const generateCSVContent = (invoices: any[]) => {
    // QuickBooks CSV format headers
    const headers = [
      "InvoiceNo",
      "Customer",
      "InvoiceDate",
      "DueDate",
      "Terms",
      "Item",
      "ItemDescription",
      "ServiceDate",
      "ItemQuantity",
      "ItemRate",
      "ItemAmount",
      "CustomerNote",
      "AllowOnlineACHPayment",
      "Subtotal",
      "Tax",
      "Total",
    ];

    const rows: string[][] = [];

    invoices.forEach((invoice) => {
      const serviceTickets = invoice.serviceTickets || [];
      const lineItems = invoice.lineItems || [];
      const invoiceDate = invoice.invoiceDate
        ? new Date(invoice.invoiceDate).toLocaleDateString("en-US")
        : "";
      // Use actual due date from invoice, fallback to invoice date if not set
      const dueDate = invoice.dueDate
        ? new Date(invoice.dueDate).toLocaleDateString("en-US")
        : invoiceDate;
      const clientName = invoice.client?.companyName || "Unknown Client";
      // Extract just digits from invoice number (remove INV prefix)
      const invoiceNo = getInvoiceNumberDigits(invoice.invoiceNumber);

      // Generate memo from service tickets
      const memo = generateMemo(invoice, serviceTickets);

      // Get additional charges (line items without serviceTicketId)
      const additionalCharges = lineItems.filter(
        (item: any) => !item.serviceTicketId
      );

      // Track if this is the first row for invoice-level fields
      let isFirstRow = true;

      // If we have service tickets, use them for richer data
      if (serviceTickets.length > 0) {
        // Sort service tickets chronologically by service date
        const sortedTickets = [...serviceTickets].sort((a: any, b: any) => {
          const dateA = new Date(a.serviceDatetime || a.startDatetime || 0).getTime();
          const dateB = new Date(b.serviceDatetime || b.startDatetime || 0).getTime();
          if (dateA !== dateB) return dateA - dateB;
          // Fallback to createdAt for same-day services
          const createdA = new Date(a.createdAt || 0).getTime();
          const createdB = new Date(b.createdAt || 0).getTime();
          return createdA - createdB;
        });

        sortedTickets.forEach((ticket: any, index: number) => {
          // Get location name from the location relation or tempLocationData
          const locationName =
            ticket.location?.addressName ||
            ticket.location?.name ||
            ticket.tempLocationData?.name ||
            "";

          // Get address and state from shipping address
          const addressParts = [];
          const address = ticket.location?.address || ticket.tempLocationData?.address || "";
          const city = ticket.location?.city || ticket.tempLocationData?.city || "";
          const state = ticket.location?.state || ticket.tempLocationData?.state || "";
          if (address) addressParts.push(address);
          if (city) addressParts.push(city);
          if (state) addressParts.push(state);
          const fullAddress = addressParts.join(", ");

          // Get job ticket reference number
          const refNumber = ticket.jobTicket?.referenceNumber || "";

          // Build suffix for multiple services (-01, -02, etc.)
          const serviceSuffix = sortedTickets.length > 1
            ? `-${String(index + 1).padStart(2, "0")}`
            : "";

          // Build description: address/city/state + scope + location name + ref # with suffix
          const descParts = [];
          if (fullAddress) descParts.push(fullAddress);
          if (ticket.scope) descParts.push(ticket.scope);
          if (locationName && locationName !== city) descParts.push(locationName);
          if (refNumber) descParts.push(`Ref #${refNumber}${serviceSuffix}`);
          const itemDescription =
            descParts.length > 0 ? descParts.join(" - ") : "Art Handling Services";

          // Item is just the service type
          const item = formatServiceType(ticket.serviceType);

          // Get service date from ticket
          const serviceDate = ticket.serviceDatetime || ticket.startDatetime
            ? new Date(ticket.serviceDatetime || ticket.startDatetime).toLocaleDateString("en-US")
            : "";

          // Find matching line item by serviceTicketId for correct pricing
          const matchingLineItem = lineItems.find(
            (item: any) => item.serviceTicketId === ticket.id
          ) || {};
          const quantity = matchingLineItem.quantity || 1;
          const rate = matchingLineItem.unitPrice || matchingLineItem.rate || 0;
          const amount = quantity * rate;

          rows.push([
            invoiceNo,
            clientName,
            invoiceDate,
            dueDate,
            isFirstRow ? "Due on receipt" : "", // Terms
            item,
            itemDescription,
            serviceDate,
            String(quantity),
            String(rate),
            String(amount),
            isFirstRow ? memo : "",
            isFirstRow ? "true" : "", // AllowOnlineACHPayment
            isFirstRow ? String(invoice.subtotal || 0) : "",
            isFirstRow ? String(invoice.tax || 0) : "",
            isFirstRow ? String(invoice.total || 0) : "",
          ]);
          isFirstRow = false;
        });

        // Add additional charges (line items without serviceTicketId)
        additionalCharges.forEach((charge: any) => {
          const chargeDate = charge.date
            ? new Date(charge.date).toLocaleDateString("en-US")
            : "";

          rows.push([
            invoiceNo,
            clientName,
            invoiceDate,
            dueDate,
            "", // Terms (already set on first row)
            charge.type === "supply" ? "Supply" : charge.type === "admin_charge" ? "Admin Charge" : "Additional Charge",
            charge.description || (charge.type === "admin_charge" ? "Admin Charge" : "Additional Charge"),
            chargeDate,
            String(charge.quantity || 1),
            String(charge.rate || charge.unitPrice || 0),
            String(charge.amount || (charge.quantity || 1) * (charge.rate || charge.unitPrice || 0)),
            "", // CustomerNote (already set on first row)
            "", // AllowOnlineACHPayment (already set on first row)
            "", // Subtotal (already set on first row)
            "", // Tax (already set on first row)
            "", // Total (already set on first row)
          ]);
        });
      } else if (lineItems.length > 0) {
        // Fall back to line items if no service tickets
        // Separate service items from additional charges
        const serviceItems = lineItems.filter((item: any) => item.serviceTicketId);
        const additionalItems = lineItems.filter((item: any) => !item.serviceTicketId);

        // Process service-related line items
        serviceItems.forEach((item: any) => {
          rows.push([
            invoiceNo,
            clientName,
            invoiceDate,
            dueDate,
            isFirstRow ? "Due on receipt" : "", // Terms
            "Services",
            item.description || "Art Handling Services",
            "", // No service date available
            String(item.quantity || 1),
            String(item.unitPrice || item.rate || 0),
            String(
              item.amount ||
                (item.quantity || 1) * (item.unitPrice || item.rate || 0)
            ),
            isFirstRow ? invoice.notes || "" : "",
            isFirstRow ? "true" : "", // AllowOnlineACHPayment
            isFirstRow ? String(invoice.subtotal || 0) : "",
            isFirstRow ? String(invoice.tax || 0) : "",
            isFirstRow ? String(invoice.total || 0) : "",
          ]);
          isFirstRow = false;
        });

        // Process additional charges
        additionalItems.forEach((item: any) => {
          const chargeDate = item.date
            ? new Date(item.date).toLocaleDateString("en-US")
            : "";

          rows.push([
            invoiceNo,
            clientName,
            invoiceDate,
            dueDate,
            isFirstRow ? "Due on receipt" : "", // Terms
            item.type === "supply" ? "Supply" : item.type === "admin_charge" ? "Admin Charge" : "Additional Charge",
            item.description || (item.type === "admin_charge" ? "Admin Charge" : "Additional Charge"),
            chargeDate,
            String(item.quantity || 1),
            String(item.unitPrice || item.rate || 0),
            String(
              item.amount ||
                (item.quantity || 1) * (item.unitPrice || item.rate || 0)
            ),
            isFirstRow ? invoice.notes || "" : "",
            isFirstRow ? "true" : "", // AllowOnlineACHPayment
            isFirstRow ? String(invoice.subtotal || 0) : "",
            isFirstRow ? String(invoice.tax || 0) : "",
            isFirstRow ? String(invoice.total || 0) : "",
          ]);
          isFirstRow = false;
        });
      } else {
        // Invoice with no line items or service tickets - create a single row
        rows.push([
          invoiceNo,
          clientName,
          invoiceDate,
          dueDate,
          isFirstRow ? "Due on receipt" : "", // Terms
          "Services",
          invoice.description || "Art Handling Services",
          "", // No service date available
          "1",
          String(invoice.subtotal || invoice.total || 0),
          String(invoice.subtotal || invoice.total || 0),
          isFirstRow ? invoice.notes || "" : "",
          isFirstRow ? "true" : "", // AllowOnlineACHPayment
          isFirstRow ? String(invoice.subtotal || 0) : "",
          isFirstRow ? String(invoice.tax || 0) : "",
          isFirstRow ? String(invoice.total || 0) : "",
        ]);
      }
    });

    // Escape CSV values
    const escapeCSV = (value: string) => {
      if (value.includes(",") || value.includes('"') || value.includes("\n")) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map(escapeCSV).join(",")),
    ].join("\n");

    return csvContent;
  };

  // Handle CSV export
  const handleExportCSV = async () => {
    if (selectedInvoiceIds.length === 0) {
      message.warning("Please select at least one invoice to export");
      return;
    }

    try {
      setIsExporting(true);

      // Get full invoice data for export
      const invoicesData = await getInvoicesForExport(selectedInvoiceIds);

      // Generate filename
      const filename = generateExportFilename();

      // Generate CSV content
      const csvContent = generateCSVContent(invoicesData);

      // Create and download the file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Only mark draft invoices as sent
      const draftInvoiceIds = invoicesData
        .filter((inv: any) => inv.status === "draft" || inv.status === "pending")
        .map((inv: any) => inv.id);

      if (draftInvoiceIds.length > 0) {
        const result = await bulkSendMutation.mutateAsync({
          ids: draftInvoiceIds,
          exportFilename: filename,
        });

        if (result.failed && result.failed.length > 0) {
          message.warning(
            `Exported ${selectedInvoiceIds.length} invoices. ${result.success.length} draft(s) marked as sent, ${result.failed.length} failed.`
          );
        } else {
          message.success(
            `Exported ${selectedInvoiceIds.length} invoice(s). ${draftInvoiceIds.length} draft(s) marked as sent.`
          );
        }
      } else {
        message.success(
          `Successfully exported ${selectedInvoiceIds.length} invoice(s)`
        );
      }

      // Clear selection and refresh
      setSelectedInvoiceIds([]);
      refetch();
    } catch (error: any) {
      console.error("Export error:", error);
      message.error(
        `Failed to export invoices: ${error?.message || "Unknown error"}`
      );
    } finally {
      setIsExporting(false);
    }
  };

  // Generate CSV content for storage invoices (QuickBooks format)
  const generateStorageCSVContent = (invoices: StorageInvoice[]) => {
    const headers = [
      "InvoiceNo",
      "Customer",
      "InvoiceDate",
      "DueDate",
      "Terms",
      "Item",
      "ItemDescription",
      "ServiceDate",
      "ItemQuantity",
      "ItemRate",
      "ItemAmount",
      "CustomerNote",
      "AllowOnlineACHPayment",
      "Subtotal",
      "Tax",
      "Total",
    ];

    const rows: string[][] = [];

    invoices.forEach((invoice) => {
      const invoiceDate = invoice.invoiceDate
        ? new Date(invoice.invoiceDate).toLocaleDateString("en-US")
        : "";
      const clientName =
        invoice.client?.companyName ||
        (invoice.client ? getClientName(invoice.client as any) : "Unknown Client");
      const invoiceNo = (invoice.invoiceNumber || "")
        .replace(/^STG-?/i, "")
        .replace(/_ArtStorage$/i, "");
      const billingPeriod = dayjs()
        .month(invoice.billingPeriodMonth - 1)
        .year(invoice.billingPeriodYear)
        .format("MMMM YYYY");
      const total = Number(invoice.totalAmount || 0);

      // Sum total cubic volume from storage line items
      const storageItems = (invoice.lineItems || []).filter((li) => li.type === "storage");
      const totalCubicFt = storageItems.reduce(
        (sum, li) => sum + (li.billableCubicFt || li.actualCubicFt || 0),
        0
      );

      const noteParts: string[] = [];
      if (totalCubicFt > 0) noteParts.push(`${Math.round(totalCubicFt * 100) / 100} cu ft`);
      if (invoice.notes) noteParts.push(invoice.notes);
      const memo = noteParts.join("; ");

      // Due date = 5 days from export
      const dueDate = dayjs().add(5, "day").format("MM/DD/YYYY");

      // Single row per invoice: description = billing period, amount = total
      rows.push([
        invoiceNo,
        clientName,
        invoiceDate,
        dueDate,
        "Due on receipt",
        "Storage",
        billingPeriod,
        invoiceDate,
        "1",
        String(total),
        String(total),
        memo,
        "true",
        String(total),
        "0",
        String(total),
      ]);
    });

    // Escape CSV values
    const escapeCSV = (value: string) => {
      if (value.includes(",") || value.includes('"') || value.includes("\n")) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    return [
      headers.join(","),
      ...rows.map((row) => row.map(escapeCSV).join(",")),
    ].join("\n");
  };

  // Handle CSV export for storage invoices
  const handleExportStorageCSV = async () => {
    if (selectedStorageInvoiceIds.length === 0) {
      message.warning("Please select at least one invoice to export");
      return;
    }

    try {
      setIsExportingStorage(true);

      // Fetch all storage invoices (no status filter, large limit) then filter by selected IDs
      const allData = await getStorageInvoices({ limit: 500 });
      const selectedInvoices = allData.invoices.filter((inv) =>
        selectedStorageInvoiceIds.includes(inv.id)
      );

      if (selectedInvoices.length === 0) {
        message.error("Could not find selected invoices");
        return;
      }

      const filename = generateExportFilename().replace("QB_Invoice", "QB_Storage_Invoice");
      const csvContent = generateStorageCSVContent(selectedInvoices);

      // Download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Mark draft invoices as sent
      const draftInvoices = selectedInvoices.filter(
        (inv) => inv.status === StorageInvoiceStatus.DRAFT
      );

      let sentCount = 0;
      for (const inv of draftInvoices) {
        try {
          await markStorageAsSent.mutateAsync(inv.id);
          sentCount++;
        } catch {
          // continue on failure
        }
      }

      if (draftInvoices.length > 0) {
        message.success(
          `Exported ${selectedStorageInvoiceIds.length} invoice(s). ${sentCount} draft(s) marked as sent.`
        );
      } else {
        message.success(
          `Successfully exported ${selectedStorageInvoiceIds.length} storage invoice(s)`
        );
      }

      setSelectedStorageInvoiceIds([]);
      refetchStorage();
    } catch (error: any) {
      console.error("Storage export error:", error);
      message.error(
        `Failed to export invoices: ${error?.message || "Unknown error"}`
      );
    } finally {
      setIsExportingStorage(false);
    }
  };

  // Storage invoice action handlers
  const handleStorageMarkAsSent = async (invoice: StorageInvoice) => {
    try {
      await markStorageAsSent.mutateAsync(invoice.id);
      message.success("Invoice marked as sent");
    } catch {
      message.error("Failed to mark invoice as sent");
    }
  };

  const handleStorageMarkAsPaid = async () => {
    if (!selectedStorageInvoiceForPaid) return;
    try {
      await markStorageAsPaid.mutateAsync({
        id: selectedStorageInvoiceForPaid.id,
        paidAmount: storagePaidAmount,
        paidDate: storagePaidDate.toISOString(),
      });
      message.success("Invoice marked as paid");
      setStoragePaidModalVisible(false);
      setSelectedStorageInvoiceForPaid(null);
    } catch {
      message.error("Failed to mark invoice as paid");
    }
  };

  const handleStorageVoid = async (invoice: StorageInvoice) => {
    try {
      await updateStorageInvoiceMutation.mutateAsync({
        id: invoice.id,
        updates: { status: StorageInvoiceStatus.CANCELLED },
      });
      message.success("Invoice voided");
    } catch {
      message.error("Failed to void invoice");
    }
  };

  const handleStorageDelete = async (invoice: StorageInvoice) => {
    try {
      await deleteStorageInvoiceMutation.mutateAsync(invoice.id);
      message.success("Invoice deleted");
    } catch {
      message.error("Failed to delete invoice");
    }
  };

  // Handle select all for invoices (works on any tab)
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const invoiceIds = filteredInvoices.map((inv) => inv.id);
      setSelectedInvoiceIds(invoiceIds);
    } else {
      setSelectedInvoiceIds([]);
    }
  };

  // Handle individual invoice selection
  const handleSelectInvoice = (invoiceId: string, checked: boolean) => {
    if (checked) {
      setSelectedInvoiceIds((prev) => [...prev, invoiceId]);
    } else {
      setSelectedInvoiceIds((prev) => prev.filter((id) => id !== invoiceId));
    }
  };

  const handleDeleteInvoice = async (invoiceId: string) => {
    try {
      setDeletingInvoiceId(invoiceId);
      await deleteInvoiceMutation.mutateAsync(invoiceId);
      message.success({
        content: "Invoice deleted successfully! 🗑️",
        duration: 3,
      });
      refetch();
    } catch (error: any) {
      message.error({
        content: `Failed to delete invoice: ${
          error?.message || "Unknown error"
        }`,
        duration: 5,
      });
    } finally {
      setDeletingInvoiceId(null);
    }
  };

  const invoices = invoicesData?.invoices || [];

  // Filter invoices based on search text and status
  const filteredInvoices = invoices.filter((invoice) => {
    const matchesSearch =
      (invoice.invoiceNumber || invoice.id)
        .toLowerCase()
        .includes(searchText.toLowerCase()) ||
      invoice.client.toLowerCase().includes(searchText.toLowerCase());

    const matchesStatus =
      activeTab === "all" ||
      (activeTab === "pending" &&
        (invoice.status === "draft" || invoice.status === "pending")) ||
      (activeTab === "invoiced" && invoice.status === "sent") ||
      (activeTab === "paid" && invoice.status === "paid") ||
      (activeTab === "overdue" && invoice.status === "overdue");

    return matchesSearch && matchesStatus;
  });

  // Check if we're on the draft tab
  const isDraftTab = activeTab === "pending";

  // Get all invoices for select all calculation (now works on any tab)
  const allSelected =
    filteredInvoices.length > 0 &&
    filteredInvoices.every((inv) => selectedInvoiceIds.includes(inv.id));
  const someSelected =
    filteredInvoices.some((inv) => selectedInvoiceIds.includes(inv.id)) &&
    !allSelected;

  // Storage invoices - filter by search
  const storageInvoices = storageInvoicesData?.invoices || [];
  const filteredStorageInvoices = storageInvoices.filter((invoice) => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    const clientName =
      invoice.client?.companyName ||
      (invoice.client ? getClientName(invoice.client as any) : "");
    return (
      (invoice.invoiceNumber || "").toLowerCase().includes(search) ||
      clientName.toLowerCase().includes(search)
    );
  });

  const allStorageSelected =
    filteredStorageInvoices.length > 0 &&
    filteredStorageInvoices.every((inv) =>
      selectedStorageInvoiceIds.includes(inv.id)
    );
  const someStorageSelected =
    filteredStorageInvoices.some((inv) =>
      selectedStorageInvoiceIds.includes(inv.id)
    ) && !allStorageSelected;

  const handleSelectAllStorage = (checked: boolean) => {
    if (checked) {
      setSelectedStorageInvoiceIds(
        filteredStorageInvoices.map((inv) => inv.id)
      );
    } else {
      setSelectedStorageInvoiceIds([]);
    }
  };

  const handleSelectStorageInvoice = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedStorageInvoiceIds((prev) => [...prev, id]);
    } else {
      setSelectedStorageInvoiceIds((prev) => prev.filter((i) => i !== id));
    }
  };

  const getStorageStatusTag = (status: StorageInvoiceStatus) => {
    const config: Record<StorageInvoiceStatus, { color: string; label: string }> = {
      [StorageInvoiceStatus.DRAFT]: { color: "blue", label: "Draft" },
      [StorageInvoiceStatus.SENT]: { color: "purple", label: "Sent" },
      [StorageInvoiceStatus.PAID]: { color: "green", label: "Paid" },
      [StorageInvoiceStatus.CANCELLED]: { color: "gray", label: "Cancelled" },
    };
    const { color, label } = config[status];
    return <Tag color={color}>{label}</Tag>;
  };

  const getStorageActionItems = (invoice: StorageInvoice) => {
    const items: any[] = [];
    if (invoice.status === StorageInvoiceStatus.DRAFT) {
      items.push({
        key: "send",
        label: "Mark as Sent",
        icon: <MailOutlined />,
        onClick: () => handleStorageMarkAsSent(invoice),
      });
    }
    if (
      invoice.status === StorageInvoiceStatus.DRAFT ||
      invoice.status === StorageInvoiceStatus.SENT
    ) {
      items.push({
        key: "paid",
        label: "Mark as Paid",
        icon: <CheckCircleOutlined />,
        onClick: () => {
          setSelectedStorageInvoiceForPaid(invoice);
          setStoragePaidAmount(Number(invoice.totalAmount) || 0);
          setStoragePaidDate(dayjs());
          setStoragePaidModalVisible(true);
        },
      });
      items.push({
        key: "cancel",
        label: "Void Invoice",
        icon: <StopOutlined />,
        danger: true,
        onClick: () => handleStorageVoid(invoice),
      });
    }
    if (invoice.status === StorageInvoiceStatus.DRAFT) {
      items.push({
        key: "delete",
        label: "Delete",
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => handleStorageDelete(invoice),
      });
    }
    return items;
  };

  const storageColumns = [
    {
      title: (
        <Checkbox
          checked={allStorageSelected}
          indeterminate={someStorageSelected}
          onChange={(e) => handleSelectAllStorage(e.target.checked)}
        />
      ),
      key: "select",
      width: 50,
      render: (_: any, record: StorageInvoice) => (
        <Checkbox
          checked={selectedStorageInvoiceIds.includes(record.id)}
          onChange={(e) =>
            handleSelectStorageInvoice(record.id, e.target.checked)
          }
        />
      ),
    },
    {
      title: "Invoice #",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      width: 160,
    },
    {
      title: "Client",
      key: "client",
      width: 200,
      render: (_: any, record: StorageInvoice) =>
        record.client?.companyName ||
        (record.client ? getClientName(record.client as any) : "Unknown"),
    },
    {
      title: "Billing Period",
      key: "billingPeriod",
      width: 130,
      render: (_: any, record: StorageInvoice) =>
        dayjs()
          .month(record.billingPeriodMonth - 1)
          .year(record.billingPeriodYear)
          .format("MMM YYYY"),
    },
    {
      title: "Storage",
      dataIndex: "storageTotal",
      key: "storageTotal",
      width: 100,
      align: "right" as const,
      render: (value: number | string) => `$${Number(value || 0).toFixed(2)}`,
    },
    {
      title: "Insurance",
      dataIndex: "insuranceTotal",
      key: "insuranceTotal",
      width: 100,
      align: "right" as const,
      render: (value: number | string) => {
        const num = Number(value || 0);
        return num > 0 ? `$${num.toFixed(2)}` : "-";
      },
    },
    {
      title: "Total",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 110,
      align: "right" as const,
      render: (value: number | string) => (
        <span className="font-medium">${Number(value || 0).toFixed(2)}</span>
      ),
      sorter: (a: StorageInvoice, b: StorageInvoice) =>
        Number(a.totalAmount || 0) - Number(b.totalAmount || 0),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: StorageInvoiceStatus) => getStorageStatusTag(status),
    },
    {
      title: "Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      width: 110,
      render: (date: string) => (date ? dayjs(date).format("MM/DD/YYYY") : "N/A"),
    },
    {
      title: "",
      key: "actions",
      width: 50,
      render: (_: any, record: StorageInvoice) => {
        const items = getStorageActionItems(record);
        if (items.length === 0) return null;
        return (
          <Dropdown menu={{ items }} trigger={["click"]}>
            <Button type="text" icon={<MoreOutlined />} size="small" />
          </Dropdown>
        );
      },
    },
  ];

  const columns = [
    // Checkbox column - show on all tabs for CSV export
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      key: "select",
      width: 50,
      render: (_: any, record: Invoice) => (
        <Checkbox
          checked={selectedInvoiceIds.includes(record.id)}
          onChange={(e) =>
            handleSelectInvoice(record.id, e.target.checked)
          }
        />
      ),
    },
    {
      title: "Invoice Number",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      render: (invoiceNumber: string, record: Invoice) =>
        invoiceNumber || record.id,
    },
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount: number) => `$${amount?.toFixed(2) || "0.00"}`,
      sorter: (a: Invoice, b: Invoice) => (a.amount || 0) - (b.amount || 0),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: Invoice["status"]) => {
        const statusConfig = {
          draft: { color: "blue", label: "Draft" },
          pending: { color: "orange", label: "Pending" },
          sent: { color: "purple", label: "Sent" },
          paid: { color: "green", label: "Paid" },
          overdue: { color: "red", label: "Overdue" },
          cancelled: { color: "gray", label: "Cancelled" },
        };
        return (
          <Tag color={statusConfig[status]?.color || "default"}>
            {statusConfig[status]?.label || status}
          </Tag>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date: string) =>
        date ? new Date(date).toLocaleDateString() : "N/A",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: Invoice) => (
        <Space>
          <Button
            type="link"
            size="small"
            onClick={() => {
              setSelectedInvoice(record);
              setViewModalVisible(true);
            }}
          >
            View
          </Button>

          <Popconfirm
            title="Are you sure you want to delete this invoice?"
            description="This action cannot be undone."
            onConfirm={() => {
              handleDeleteInvoice(record.id);
            }}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="link"
              size="small"
              icon={<DeleteOutlined />}
              loading={deletingInvoiceId === record.id}
              danger
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const serviceTabItems = [
    { key: "all", label: "All Invoices", children: null },
    { key: "pending", label: "Draft", children: null },
    { key: "invoiced", label: "Sent", children: null },
    { key: "paid", label: "Paid", children: null },
    { key: "overdue", label: "Overdue", children: null },
  ];

  const storageTabItems = [
    { key: "all", label: "All Invoices", children: null },
    { key: "pending", label: "Draft", children: null },
    { key: "invoiced", label: "Sent", children: null },
    { key: "paid", label: "Paid", children: null },
  ];

  const tabItems = invoiceType === "storage" ? storageTabItems : serviceTabItems;

  // Function to handle viewing the PDF
  const handleViewPdf = async () => {
    if (!selectedInvoice) return;

    try {
      setIsPdfLoading(true);
      const pdfBlob = await getInvoicePdf(selectedInvoice.id);

      // Create a URL for the blob with proper MIME type
      const pdfUrl = URL.createObjectURL(
        new Blob([pdfBlob], { type: "application/pdf" })
      );

      // Try to open in a new tab
      const newWindow = window.open(pdfUrl, "_blank");

      // If opening in a new tab fails, offer a download option
      if (
        !newWindow ||
        newWindow.closed ||
        typeof newWindow.closed === "undefined"
      ) {
        // Create a download link
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.download = `invoice-${
          selectedInvoice.invoiceNumber || selectedInvoice.id
        }.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        message.info(
          "Invoice PDF downloaded. Some browsers may block opening PDFs directly."
        );
      } else {
        message.success("Invoice PDF opened successfully");
      }

      // Clean up the URL object after a delay
      setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
      }, 30000);
    } catch (error: any) {
      console.error("PDF error:", error);

      // Extract error details from the response
      let errorMessage = error?.message || "";
      let errorDetail = "";

      // Check if the error is from the backend with additional details
      if (error?.response?.data) {
        const responseData = error.response.data;
        errorMessage = responseData.message || errorMessage;
        errorDetail = responseData.detail || "";
      }

      // Handle specific error cases
      if (
        errorMessage.includes("not found in QuickBooks") ||
        errorMessage.includes("Object Not Found")
      ) {
        Modal.error({
          title: "Invoice Not Found in QuickBooks",
          content: (
            <div>
              <p>
                {errorDetail ||
                  "This invoice could not be found in QuickBooks. This could be because:"}
              </p>
              <ul className="list-disc pl-5 mt-2">
                <li>
                  The invoice has been deleted or made inactive in QuickBooks
                </li>
                <li>The invoice was never properly synced with QuickBooks</li>
                <li>There's a connection issue with QuickBooks</li>
              </ul>
              <p className="mt-3">
                Please check the invoice status in QuickBooks or contact your
                administrator.
              </p>
            </div>
          ),
        });
      } else if (errorMessage.includes("QuickBooks")) {
        message.error(
          `QuickBooks error: ${
            errorDetail ||
            "Unable to retrieve the invoice PDF. Please check your QuickBooks connection."
          }`
        );
      } else {
        message.error(
          `Failed to load invoice PDF: ${errorMessage || "Unknown error"}`
        );
      }
    } finally {
      setIsPdfLoading(false);
    }
  };

  if (error) {
    return (
      <div>
        <PageMeta
          title="Invoicing | Art Handler"
          description="Manage invoices for art handling services"
        />
        <BillingSubNav activeKey="invoicing" />
        <Alert
          message="Error Loading Invoices"
          description="Failed to load invoice data. Please check your connection to the backend."
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div>
      <PageMeta
        title="Invoicing | Art Handler"
        description="Manage invoices for art handling services"
      />

      <BillingSubNav activeKey="invoicing" />

      {/* Invoice Type Selector */}
      <div className="mb-4">
        <Segmented
          value={invoiceType}
          onChange={(val) => {
            setInvoiceType(val as "service" | "storage");
            setActiveTab("all");
          }}
          options={[
            {
              label: (
                <div className="flex items-center gap-2 px-2">
                  <FileTextOutlined />
                  <span>Service Invoices</span>
                </div>
              ),
              value: "service",
            },
            {
              label: (
                <div className="flex items-center gap-2 px-2">
                  <DatabaseOutlined />
                  <span>Storage Invoices</span>
                </div>
              ),
              value: "storage",
            },
          ]}
          size="large"
        />
      </div>

      {invoiceType === "service" && (
        <>
          {/* Payment Processor Status */}
          <Card className="mb-6 shadow-sm">
            <div className="flex items-center space-x-3">
              <CheckCircleOutlined className="text-green-500 text-2xl" />
              <div>
                <div className="font-semibold text-green-700">
                  Payment Processor Connected
                </div>
                <div className="text-sm text-gray-600">
                  Stripe — Payment links are generated automatically with invoices
                </div>
              </div>
            </div>
          </Card>
        </>
      )}

      {/* Controls */}
      <Card className="mb-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search invoices..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="max-w-md"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            {invoiceType === "service" && (
              <RangePicker className="w-full sm:w-auto" />
            )}
            {invoiceType === "service" && selectedInvoiceIds.length > 0 && (
              <>
                <Button
                  type="primary"
                  size="large"
                  icon={<SendOutlined />}
                  onClick={() => setSendModalVisible(true)}
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 border-0 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Send Invoices ({selectedInvoiceIds.length})
                </Button>
                <Button
                  size="large"
                  icon={<DownloadOutlined />}
                  onClick={handleExportCSV}
                  loading={isExporting}
                >
                  Export CSV ({selectedInvoiceIds.length})
                </Button>
              </>
            )}
            {invoiceType === "storage" && selectedStorageInvoiceIds.length > 0 && (
              <Button
                size="large"
                icon={<DownloadOutlined />}
                onClick={handleExportStorageCSV}
                loading={isExportingStorage}
              >
                Export CSV ({selectedStorageInvoiceIds.length})
              </Button>
            )}
            {invoiceType === "service" && (
              <Button
                type="primary"
                size="large"
                icon={<PlusOutlined />}
                onClick={() => navigate("/billing/invoicing/create")}
                className="bg-gradient-to-r from-blue-500 to-blue-600 border-0 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                Create New Invoice
              </Button>
            )}
            {invoiceType === "storage" && (
              <Tooltip title="Refresh">
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => refetchStorage()}
                  loading={isLoadingStorage}
                />
              </Tooltip>
            )}
          </div>
        </div>
      </Card>

      {/* Invoice List */}
      <Card className="shadow-sm">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          className="mb-4"
        />

        {/* Service invoices selection banner */}
        {invoiceType === "service" && selectedInvoiceIds.length > 0 && (
          <Alert
            message={
              <div className="flex items-center justify-between">
                <span>
                  <strong>{selectedInvoiceIds.length} invoice(s) selected</strong>
                  {" "}- Send with payment link or export as CSV.
                  {isDraftTab && (
                    <span className="text-gray-500 ml-1">
                      (Draft invoices will be marked as Sent)
                    </span>
                  )}
                </span>
                <Button
                  type="link"
                  size="small"
                  onClick={() => setSelectedInvoiceIds([])}
                >
                  Clear Selection
                </Button>
              </div>
            }
            type="info"
            showIcon
            className="mb-4"
          />
        )}

        {/* Storage invoices selection banner */}
        {invoiceType === "storage" && selectedStorageInvoiceIds.length > 0 && (
          <Alert
            message={
              <div className="flex items-center justify-between">
                <span>
                  <strong>{selectedStorageInvoiceIds.length} invoice(s) selected</strong>
                  {" "}- Export as CSV for QuickBooks.
                  {isDraftTab && (
                    <span className="text-gray-500 ml-1">
                      (Draft invoices will be marked as Sent on export)
                    </span>
                  )}
                </span>
                <Button
                  type="link"
                  size="small"
                  onClick={() => setSelectedStorageInvoiceIds([])}
                >
                  Clear Selection
                </Button>
              </div>
            }
            type="info"
            showIcon
            className="mb-4"
          />
        )}

        {/* Service Invoices Table */}
        {invoiceType === "service" && (
          <Spin spinning={isLoading}>
            <Table
              columns={columns}
              dataSource={filteredInvoices}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              locale={{
                emptyText: isLoading
                  ? "Loading..."
                  : "No invoices found. Connect to backend to load real data.",
              }}
            />
          </Spin>
        )}

        {/* Storage Invoices Table */}
        {invoiceType === "storage" && (
          <Spin spinning={isLoadingStorage}>
            <Table
              columns={storageColumns}
              dataSource={filteredStorageInvoices}
              rowKey="id"
              size="small"
              pagination={{
                current: storageInvoicePage,
                pageSize: storageInvoicePageSize,
                total: storageInvoicesData?.total || 0,
                showSizeChanger: true,
                showTotal: (total) => `${total} invoices`,
                onChange: (p, ps) => {
                  setStorageInvoicePage(p);
                  setStorageInvoicePageSize(ps);
                },
              }}
              locale={{
                emptyText: isLoadingStorage
                  ? "Loading..."
                  : "No storage invoices found.",
              }}
            />
          </Spin>
        )}
      </Card>

      {/* View Modal */}
      {selectedInvoice && (
        <Modal
          visible={viewModalVisible}
          onCancel={() => setViewModalVisible(false)}
          footer={[
            // <Button
            //   key="back"
            //   onClick={() => {
            //     setViewModalVisible(false);
            //     navigate("/billing/invoicing");
            //   }}
            // >
            //   Back to Invoice List
            // </Button>,
            <Button key="close" onClick={() => setViewModalVisible(false)}>
              Close
            </Button>,
            <Button
              key="print"
              type="primary"
              icon={<FilePdfOutlined />}
              onClick={handleViewPdf}
              loading={isPdfLoading}
            >
              View/Download PDF
            </Button>,
          ]}
          width={900}
          title={
            <div className="flex items-center space-x-3">
              <FileTextOutlined className="text-blue-600" />
              <span className="text-xl font-semibold">Invoice Details</span>
            </div>
          }
          className="invoice-detail-modal"
        >
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-lg">
            {/* Header Section */}
            <div className="bg-white rounded-lg p-6 shadow-sm mb-6">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">
                    {selectedInvoice.invoiceNumber || selectedInvoice.id}
                  </h2>
                  <div className="text-gray-600">
                    <div className="flex items-center space-x-2 mb-1">
                      <UserOutlined className="text-blue-500" />
                      <span className="font-medium">Client:</span>
                      <span>{selectedInvoice.client}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CalendarOutlined className="text-green-500" />
                      <span className="font-medium">Date:</span>
                      <span>
                        {selectedInvoice.date
                          ? new Date(selectedInvoice.date).toLocaleDateString()
                          : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    ${selectedInvoice.amount?.toFixed(2) || "0.00"}
                  </div>
                  <Tag
                    color={
                      selectedInvoice.status === "paid"
                        ? "green"
                        : selectedInvoice.status === "sent"
                        ? "blue"
                        : selectedInvoice.status === "overdue"
                        ? "red"
                        : "orange"
                    }
                    className="text-sm px-3 py-1"
                  >
                    {selectedInvoice.status?.toUpperCase() || "DRAFT"}
                  </Tag>
                </div>
              </div>
            </div>

            {/* Details Section */}
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FileTextOutlined className="mr-2 text-blue-500" />
                Invoice Information
              </h3>
              <Descriptions
                bordered
                column={2}
                size="middle"
                className="custom-descriptions"
              >
                <Descriptions.Item label="Invoice Number" span={1}>
                  <span className="font-mono text-blue-600">
                    {selectedInvoice.invoiceNumber || selectedInvoice.id}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label="Status" span={1}>
                  <Tag
                    color={
                      selectedInvoice.status === "paid"
                        ? "green"
                        : selectedInvoice.status === "sent"
                        ? "blue"
                        : selectedInvoice.status === "overdue"
                        ? "red"
                        : "orange"
                    }
                  >
                    {selectedInvoice.status?.toUpperCase() || "DRAFT"}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Client" span={1}>
                  <div className="flex items-center">
                    <UserOutlined className="mr-2 text-gray-400" />
                    {selectedInvoice.client}
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Amount" span={1}>
                  <span className="text-lg font-semibold text-green-600">
                    ${selectedInvoice.amount?.toFixed(2) || "0.00"}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label="Invoice Date" span={1}>
                  <div className="flex items-center">
                    <CalendarOutlined className="mr-2 text-gray-400" />
                    {selectedInvoice.date
                      ? new Date(selectedInvoice.date).toLocaleDateString()
                      : "N/A"}
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Due Date" span={1}>
                  <div className="flex items-center">
                    <CalendarOutlined className="mr-2 text-gray-400" />
                    {selectedInvoice.date
                      ? new Date(
                          new Date(selectedInvoice.date).getTime() +
                            30 * 24 * 60 * 60 * 1000
                        ).toLocaleDateString()
                      : "N/A"}
                  </div>
                </Descriptions.Item>
              </Descriptions>
            </div>

            {/* Payment Link Section */}
            <div className="bg-white rounded-lg p-6 shadow-sm mt-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <LinkOutlined className="mr-2 text-green-500" />
                Payment Link
              </h3>
              {(selectedInvoice as any).stripePaymentLinkUrl ? (
                <div className="space-y-3">
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="text-xs text-green-600 break-all mb-2">
                      {(selectedInvoice as any).stripePaymentLinkUrl}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => {
                          navigator.clipboard.writeText((selectedInvoice as any).stripePaymentLinkUrl);
                          message.success("Payment link copied!");
                        }}
                      >
                        Copy Link
                      </Button>
                      <Button
                        size="small"
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={() => {
                          // Open email with payment link
                          const subject = encodeURIComponent(`Invoice ${selectedInvoice.invoiceNumber} — Payment`);
                          const body = encodeURIComponent(
                            `Hi,\n\nPlease find attached invoice ${selectedInvoice.invoiceNumber} for $${selectedInvoice.amount?.toFixed(2)}.\n\nPay online: ${(selectedInvoice as any).stripePaymentLinkUrl}\n\nThank you,\nArt Handler Collective`
                          );
                          window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
                        }}
                      >
                        Email with Link
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-sm text-yellow-700 mb-2">No payment link generated yet.</p>
                  <Button
                    size="small"
                    type="primary"
                    icon={<LinkOutlined />}
                    onClick={async () => {
                      try {
                        const result = await createPaymentLink(selectedInvoice.id);
                        message.success("Payment link created!");
                        navigator.clipboard.writeText(result.paymentLinkUrl);
                        message.info("Link copied to clipboard");
                        // Refresh the invoice list
                        refetch();
                      } catch (e: any) {
                        message.error(e?.response?.data?.message || "Failed to create link");
                      }
                    }}
                  >
                    Generate Payment Link
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
      {/* Send Invoice Modal */}
      <Modal
        title="Send Invoices"
        open={sendModalVisible}
        onCancel={() => {
          setSendModalVisible(false);
          setInvoiceToPreview(null);
          setPreviewPaymentLink(null);
        }}
        width={700}
        footer={null}
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            {selectedInvoiceIds.length} invoice(s) selected. Preview each invoice, generate payment links, and send to clients.
          </p>

          {/* Invoice list with actions */}
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {filteredInvoices
              ?.filter((inv: any) => selectedInvoiceIds.includes(inv.id))
              .map((invoice: any) => (
                <div
                  key={invoice.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                >
                  <div>
                    <div className="font-medium">{invoice.invoiceNumber}</div>
                    <div className="text-sm text-gray-500">
                      {invoice.client} — ${Number(invoice.amount).toFixed(2)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="small"
                      icon={<FileTextOutlined />}
                      onClick={async () => {
                        try {
                          const data = await getInvoice(invoice.id);
                          setInvoiceToPreview(data);
                          // Check if payment link already exists
                          if (data.stripePaymentLinkUrl) {
                            setPreviewPaymentLink(data.stripePaymentLinkUrl);
                          } else {
                            setPreviewPaymentLink(null);
                          }
                        } catch (e) {
                          message.error("Failed to load invoice");
                        }
                      }}
                    >
                      Preview
                    </Button>
                    <Button
                      size="small"
                      type="primary"
                      icon={<LinkOutlined />}
                      loading={generatingLink}
                      onClick={async () => {
                        try {
                          setGeneratingLink(true);
                          const result = await createPaymentLink(invoice.id);
                          message.success("Payment link created!");
                          setPreviewPaymentLink(result.paymentLinkUrl);
                          // Load preview
                          const data = await getInvoice(invoice.id);
                          setInvoiceToPreview(data);
                        } catch (e: any) {
                          message.error(e?.response?.data?.message || "Failed to create payment link");
                        } finally {
                          setGeneratingLink(false);
                        }
                      }}
                    >
                      Get Link
                    </Button>
                  </div>
                </div>
              ))}
          </div>

          {/* Invoice Preview */}
          {invoiceToPreview && (
            <div className="border rounded-lg p-4 bg-white mt-4">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h4 className="text-lg font-semibold">{invoiceToPreview.invoiceNumber}</h4>
                  <p className="text-sm text-gray-500">
                    {invoiceToPreview.client?.companyName} — Total: ${Number(invoiceToPreview.total).toFixed(2)}
                  </p>
                </div>
                <Tag color={invoiceToPreview.status === 'paid' ? 'success' : invoiceToPreview.status === 'sent' ? 'warning' : 'default'}>
                  {invoiceToPreview.status}
                </Tag>
              </div>

              {/* Line Items */}
              {invoiceToPreview.lineItems && invoiceToPreview.lineItems.length > 0 && (
                <table className="w-full text-sm mb-4">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-1">Description</th>
                      <th className="text-right py-1">Qty</th>
                      <th className="text-right py-1">Rate</th>
                      <th className="text-right py-1">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceToPreview.lineItems.map((item: any, i: number) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="py-1">{item.description}</td>
                        <td className="text-right py-1">{item.quantity}</td>
                        <td className="text-right py-1">${Number(item.unitPrice || item.rate || 0).toFixed(2)}</td>
                        <td className="text-right py-1">${Number((item.quantity || 1) * (item.unitPrice || item.rate || 0)).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Payment Link */}
              {previewPaymentLink && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-green-800">Stripe Payment Link</div>
                      <div className="text-xs text-green-600 truncate max-w-md">{previewPaymentLink}</div>
                    </div>
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => {
                        navigator.clipboard.writeText(previewPaymentLink);
                        message.success("Payment link copied!");
                      }}
                    >
                      Copy
                    </Button>
                  </div>
                </div>
              )}

              {!previewPaymentLink && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
                  <div className="text-sm text-yellow-800">
                    No payment link yet — click "Get Link" to generate one.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Send All Button */}
          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <Button onClick={() => setSendModalVisible(false)}>
              Close
            </Button>
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={sendingInvoices}
              onClick={async () => {
                try {
                  setSendingInvoices(true);
                  // Mark all selected as sent
                  await bulkSendMutation.mutateAsync({
                    ids: selectedInvoiceIds,
                  });
                  message.success(`${selectedInvoiceIds.length} invoice(s) marked as sent!`);
                  setSelectedInvoiceIds([]);
                  setSendModalVisible(false);
                  setInvoiceToPreview(null);
                  refetch();
                } catch (e) {
                  message.error("Failed to send invoices");
                } finally {
                  setSendingInvoices(false);
                }
              }}
            >
              Mark as Sent ({selectedInvoiceIds.length})
            </Button>
          </div>
        </div>
      </Modal>

      {/* Storage Invoice - Mark as Paid Modal */}
      <Modal
        title="Mark Storage Invoice as Paid"
        open={storagePaidModalVisible}
        onCancel={() => {
          setStoragePaidModalVisible(false);
          setSelectedStorageInvoiceForPaid(null);
        }}
        onOk={handleStorageMarkAsPaid}
        okText="Confirm Payment"
        confirmLoading={markStorageAsPaid.isPending}
      >
        {selectedStorageInvoiceForPaid && (
          <div className="space-y-4">
            <div className="p-3 bg-gray-50 rounded">
              <div className="text-sm text-gray-500">Invoice</div>
              <div className="font-medium">
                {selectedStorageInvoiceForPaid.invoiceNumber}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount Paid
              </label>
              <InputNumber
                className="w-full"
                value={storagePaidAmount}
                onChange={(v) => setStoragePaidAmount(v || 0)}
                min={0}
                precision={2}
                addonBefore="$"
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) =>
                  Number(value?.replace(/\$\s?|(,*)/g, "")) || 0
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Date
              </label>
              <DatePicker
                className="w-full"
                value={storagePaidDate}
                onChange={(date) => date && setStoragePaidDate(date)}
                format="MM/DD/YYYY"
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ScreenInvoicing;
